import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useSelector } from "react-redux";
import { isEqual } from "lodash-es";

import utils from "../../../../utils";
import { useObjectMemo, usePrevious } from "../../../hooks";
import { useLibrary } from "../../../providers";
import { countryCodeToFlag } from "../../../utils/internationalPhoneNumber";
import Address from "../../../../Address";
import filters from "../../../../filters";
import { maskAccountNumber } from "../../../utils/masks/accountDestination.mask";
import { useRecipient } from "../../entities/recipients";
import { useUser } from "../../entities";
import { useOneSwiftWireTemplate } from "../../entities/swiftwireTemplates/hooks";
import { useCreateOrUpdateTemplateRequest as useCreateOrUpdateSwiftWireTemplate } from "../../entities/swiftwireTemplates/requests";

import { wireTypeSelectorForm } from "./WireTransferContainer.form";
import {
  createDefaultValues,
  getInternationalWireRecurringRule,
} from "./InternationalWire.form";
import { useInternationalWireContext } from "./InternationalWireFormProvider";
import {
  useInternationalWiresContext,
  useScheduledInternationalWiresContext,
} from "./providers";
import {
  useCreateInternationalWire,
  useCreateScheduledInternationalWire,
  useCreateInternationalWireViaTemplate,
  useFetchExchangeRateQuote,
} from "./requests";
import { useUpsertInternationalTemplateContext } from "./UpsertInternationalTemplateFormProvider";
import { transformApiSwiftWireTemplateToForm } from "./upsertInternationalTemplate.form";
import {
  selectFilteredSwiftWireTemplateIds,
  selectSwiftWireTemplateDetails,
} from "./selectors";

import type { UpsertInternationalTemplateFormFields } from "./upsertInternationalTemplate.form";
import type { RootState } from "../../entities";

export const useWireTypeSelector = () => {
  const form = wireTypeSelectorForm.useForm();
  const {
    values: { wireType },
    submitForm,
    resetForm,
  } = form;

  const submitMethod = useCallback(async () => {
    const result = await submitForm();
    return result.success;
  }, [submitForm]);

  return useObjectMemo({
    submitMethod,
    resetForm,
    wireType,
    form,
  });
};

export const useCreateInternationalWireForm = () => {
  const internationalWireForm = useInternationalWireContext();
  const { formState, getValues, setValue, watch } = internationalWireForm;
  const { createInternationalWire, loading: creatingWire } =
    useCreateInternationalWire();
  const { createScheduledInternationalWire, loading: creatingScheduledWire } =
    useCreateScheduledInternationalWire();
  const {
    createInternationalWireViaTemplate,
    loading: creatingWireViaTemplate,
  } = useCreateInternationalWireViaTemplate();
  const loading =
    creatingWire || creatingScheduledWire || creatingWireViaTemplate;
  const recipientId = getValues("recipientId");
  const recipient = useRecipient(
    recipientId as API.RecipientId,
  ) as API.Recipient;
  const user = useUser();
  const recipientRef = useRef(recipient);
  const { refresh } = useInternationalWiresContext();
  const { refresh: refreshScheduled } = useScheduledInternationalWiresContext();

  useEffect(() => {
    recipientRef.current = recipient;
  }, [recipient]);

  const recipientCurrency = watch("recipientCurrency");
  const prevRecipientCurrencyRef = useRef(recipientCurrency);

  useEffect(() => {
    // only reset quote when the currency changes, or we can lose a valid quote
    if (prevRecipientCurrencyRef.current === recipientCurrency) return;
    prevRecipientCurrencyRef.current = recipientCurrency;
    setValue("quote", undefined);
  }, [recipientCurrency, setValue]);

  const handleCreate = useCallback(
    async (
      idempotencyKey: UUID,
      deviceId?: string,
      deviceRequestId?: string,
      onSuccess?: () => void,
      onError?: () => void,
    ) => {
      const values = getValues();
      const isTemplateWire = !!values.swiftWireTemplate;
      const recurringRule = getInternationalWireRecurringRule(values);

      if (recurringRule && recipientRef.current) {
        const swiftTransaction = await createScheduledInternationalWire(
          idempotencyKey,
          recipientRef.current,
          deviceId,
          deviceRequestId,
          { onError },
        );
        if (swiftTransaction?.id) {
          setValue("id", swiftTransaction.id);
          setValue("scheduledWire", swiftTransaction);
          onSuccess?.();
        }
        refresh();
        refreshScheduled();
      } else if (isTemplateWire) {
        const swiftTransaction = await createInternationalWireViaTemplate(
          idempotencyKey,
          deviceId,
          deviceRequestId,
          { onSuccess, onError },
        );
        if (swiftTransaction?.id) {
          setValue("id", swiftTransaction.id);
          if (swiftTransaction.hold_until !== undefined) {
            setValue("hold_until", swiftTransaction.hold_until);
          }
        }
        refresh();
      } else if (user) {
        const swiftTransaction = await createInternationalWire(
          idempotencyKey,
          recipientRef?.current,
          user,
          deviceId,
          deviceRequestId,
          {
            onSuccess,
          },
        );
        if (swiftTransaction?.id) {
          setValue("id", swiftTransaction.id);
          if (swiftTransaction.hold_until !== undefined) {
            setValue("hold_until", swiftTransaction.hold_until);
          }
        }
        refresh();
      }
    },
    [
      createInternationalWire,
      createScheduledInternationalWire,
      createInternationalWireViaTemplate,
      getValues,
      recipientRef,
      user,
      refresh,
      refreshScheduled,
      setValue,
    ],
  );

  return useObjectMemo({
    ...internationalWireForm,
    handleCreate,
    formState,
    loading,
  });
};

export const useEffectiveConversionRate = (): number | undefined => {
  const { watch } = useInternationalWireContext();
  const exchangeRateData = watch("quote");
  const effectiveConversionRate = useMemo(() => {
    const rate = parseFloat(exchangeRateData?.rate || "NaN");
    if (Number.isNaN(rate)) {
      return undefined;
    }
    if (
      exchangeRateData?.converted_currency !==
      exchangeRateData?.settlement_currency
    ) {
      return 1 / rate;
    }
    return rate;
  }, [exchangeRateData]);
  return effectiveConversionRate;
};

/**
 * Syncs the selected swift wire template from Redux into the form.
 */
export const useSwiftWireTemplateSelector = () => {
  const { watch, reset, getValues, setValue, formState } =
    useInternationalWireContext();
  const templatesById = useSelector(
    (state: RootState) => state.swiftwireTemplates.byId,
  );
  const swiftWireTemplateId = watch("swiftWireTemplateId");

  // Sync form's template snapshot with Redux when the store updates
  // (e.g., user edited a template on the templates management page)
  useEffect(() => {
    if (swiftWireTemplateId && templatesById[swiftWireTemplateId]) {
      setValue("swiftWireTemplate", templatesById[swiftWireTemplateId]);
    }
  }, [swiftWireTemplateId, templatesById, setValue]);

  const onSelectTemplate = useCallback(
    (templateId: API.SwiftWireTemplateId) => {
      const currentCurrency = getValues("recipientCurrency");
      const defaults = createDefaultValues();

      if (templateId === swiftWireTemplateId) {
        // Deselect: clear template, keep currency
        reset({
          ...defaults,
          recipientCurrency: currentCurrency,
          hasFormBeenStarted: true,
        });
        return;
      }

      // Resolve template from Redux
      const template = templatesById[templateId];
      if (!template) {
        return;
      }

      const templateValues: Record<string, unknown> = {};
      templateValues.swiftWireTemplate = template;
      templateValues.fromAccountId = template.from_account;
      templateValues.recipientId = template.recipient;

      if (template.wire_reason != null) {
        templateValues.wireReason = template.wire_reason;
      }
      if (template.wire_reason_description != null) {
        templateValues.wireReasonDescription = filters.humanize(
          template.wire_reason_description,
        );
      }
      if (template.amount) {
        templateValues.amount = template.amount;
      }
      if (template.memo) {
        templateValues.memo = template.memo;
      }
      if (template.note) {
        templateValues.note = template.note;
      }
      if (template.recipient_currency) {
        templateValues.recipientCurrency = template.recipient_currency;
      }
      if (template.amount_currency) {
        templateValues.currencyTransferPreference =
          template.amount_currency === "USD" ? "send" : "receive";
      }

      reset({
        ...defaults,
        recipientCurrency: currentCurrency,
        swiftWireTemplateId: templateId,
        hasFormBeenStarted: true,
        ...templateValues,
      });
    },
    [swiftWireTemplateId, reset, getValues, templatesById],
  );

  return useObjectMemo({
    selectedTemplateId: swiftWireTemplateId as
      | API.SwiftWireTemplateId
      | undefined,
    onSelectTemplate,
    error: formState.errors.swiftWireTemplateId?.message,
  });
};

/**
 * Returns which form fields the user still needs to fill in when a template
 * is selected. The UI uses these booleans to conditionally show/hide inputs.
 *
 * Mirrors the domestic wire `useConditionalDetails` pattern.
 */
export const useConditionalDetails = () => {
  const { watch } = useInternationalWireContext();
  const swiftWireTemplate = watch("swiftWireTemplate");
  const swiftWireTemplateId = watch("swiftWireTemplateId");

  const requiresAmount = !swiftWireTemplate?.amount;
  const requiresWireReason = !swiftWireTemplate?.wire_reason;
  const requiresMemo = !swiftWireTemplate?.memo;
  const requiresNote = !swiftWireTemplate?.note;

  return useObjectMemo({
    templateSelected: !!swiftWireTemplateId,
    additionalDetailsRequired:
      requiresAmount || requiresWireReason || requiresMemo || requiresNote,
    requiresAmount,
    requiresWireReason,
    requiresMemo,
    requiresNote,
  });
};

/**
 * Automatically fetches an FX quote when a template has a non-USD amount.
 *
 * When a template is selected with:
 *   - amount_currency !== "USD" (foreign currency)
 *   - amount is present
 * This hook fetches a quote and writes it into the form's `quote` field,
 * so the user immediately sees the conversion rate.
 */
export const useTemplateFxQuote = () => {
  const { watch, setValue } = useInternationalWireContext();
  const swiftWireTemplate = watch("swiftWireTemplate");
  const { fetchExchangeRateQuote, loading, errorMessage } =
    useFetchExchangeRateQuote();

  const previousTemplateIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!swiftWireTemplate) {
      previousTemplateIdRef.current = undefined;
      return;
    }

    // Only fetch when the template changes (not on every re-render)
    if (previousTemplateIdRef.current === swiftWireTemplate.id) return;
    previousTemplateIdRef.current = swiftWireTemplate.id;

    const { amount, amount_currency } = swiftWireTemplate;

    // No FX quote needed if no amount, no currency, or USD
    if (!amount || !amount_currency || amount_currency === "USD") {
      return;
    }

    // Template has a non-USD amount — fetch a quote
    // The amount on the template is in the currency's natural unit.
    const amountStr = amount.toString();

    fetchExchangeRateQuote(amount_currency, amountStr, undefined, {
      onData: (quote) => {
        setValue("quote", quote, { shouldValidate: false });
      },
    }).catch(() => {
      // Ignore errors — the user can still submit the wire without a quote.
      // The form will show an error if the quote is required for FX.
    });
  }, [swiftWireTemplate, fetchExchangeRateQuote, setValue]);

  return useObjectMemo({
    loading,
    errorMessage,
  });
};

export const useAddOrEditInternationalTemplateForm = (
  existingTemplateId?: API.SwiftWireTemplateId,
) => {
  const form = useUpsertInternationalTemplateContext();
  const { reset, setValue, setError, handleSubmit } = form;

  const existingTemplate = useOneSwiftWireTemplate(existingTemplateId);

  useEffect(() => {
    if (existingTemplate) {
      reset(transformApiSwiftWireTemplateToForm(existingTemplate));
    } else {
      reset();
    }
  }, [existingTemplate, reset]);

  const onRemoveRecipient = useCallback(() => {
    setValue("recipientId", "" as API.RecipientId);
  }, [setValue]);

  const { createOrUpdateTemplate, loading } =
    useCreateOrUpdateSwiftWireTemplate();

  const onSubmit = useCallback(
    async (onSuccess?: () => void) => {
      return handleSubmit(
        async (data) => {
          const isUpdate = !!existingTemplateId;
          const empty = isUpdate ? "" : undefined;
          let sanitizedAmount: number | undefined = data.amount;
          if (data.amount && data.amountCurrency) {
            const fractionDigits =
              new Intl.NumberFormat(undefined, {
                style: "currency",
                currency: data.amountCurrency,
              }).resolvedOptions().maximumFractionDigits || 0;
            sanitizedAmount = Number(
              Number(data.amount).toFixed(fractionDigits),
            );
          }
          const payload: API.CreateOrUpdateSwiftWireTemplate.Request = {
            name: data.name,
            recipient: data.recipientId,
            from_account: data.fromAccountId,
            wire_reason: data.wireReason || empty,
            amount: sanitizedAmount || (isUpdate ? null : undefined),
            recipient_currency: data.recipientCurrency || empty,
            amount_currency: data.amountCurrency || empty,
            memo: data.memo || empty,
            note: data.note || empty,
          };
          const result = await createOrUpdateTemplate(
            payload,
            existingTemplateId,
            (fieldErrors) => {
              const apiToFormField: Record<
                string,
                keyof UpsertInternationalTemplateFormFields
              > = {
                name: "name",
                recipient: "recipientId",
                from_account: "fromAccountId",
                wire_reason: "wireReason",
                memo: "memo",
                note: "note",
                recipient_currency: "recipientCurrency",
                amount_currency: "amountCurrency",
              };
              Object.entries(fieldErrors).forEach(([apiField, message]) => {
                const formField = apiToFormField[apiField];
                if (formField) {
                  setError(formField, {
                    type: "server",
                    message,
                  });
                }
              });
            },
          ).catch(() => {
            // errors already surfaced via inline setError or toast
          });
          if (result && onSuccess) {
            onSuccess();
          }
        },
        () => {
          // validation failed — no-op, errors shown inline
        },
      )();
    },
    [handleSubmit, createOrUpdateTemplate, existingTemplateId, setError],
  );

  return useObjectMemo({
    onRemoveRecipient,
    onSubmit,
    loading,
    form,
  });
};

/**
 * Manages the collapse/expand state for a swift wire template preview card.
 */
export const useSwiftWireTemplatePreview = (initiallyCollapsed = true) => {
  const [collapsed, setCollapsed] = useState(initiallyCollapsed);
  const onToggle = useCallback(() => setCollapsed((c) => !c), []);
  return useObjectMemo({ collapsed, onToggle });
};

type SwiftWireTemplateDetailsOptions = {
  platform?: "web" | "mobile";
  wireReasons?: API.InternationalWireReason[];
};

export const useSwiftWireTemplateDetails = (
  swiftWireTemplateId: API.SwiftWireTemplateId,
  options: SwiftWireTemplateDetailsOptions = {},
) => {
  const { platform = "web", wireReasons } = options;

  const t = useLibrary("translations");
  const data = useSelector((state) =>
    selectSwiftWireTemplateDetails(state, swiftWireTemplateId),
  );

  return useMemo(() => {
    if (!data) {
      return null;
    }

    const { template, recipient, account } = data;
    const swiftDestination = recipient.swift_destination;

    // Resolve full currency name for non-USD recipient currencies (web only — Intl.DisplayNames unavailable in Hermes)
    const currencyName =
      platform === "web" &&
      template.recipient_currency &&
      template.recipient_currency !== "USD"
        ? new Intl.DisplayNames("en", { type: "currency" }).of(
            template.recipient_currency,
          )
        : undefined;

    // Format amount with local currency symbol + flag for non-USD
    let formattedAmount: string | null = null;
    if (template.amount) {
      if (template.amount_currency) {
        const base = utils.currencyFormattedValue(
          String(template.amount),
          template.amount_currency,
        );
        const flag = countryCodeToFlag(template.amount_currency.slice(0, 2));
        formattedAmount = `${base} ${flag} ${template.amount_currency}`;
      } else {
        formattedAmount = filters.currency(template.amount as Dollars, {
          hasDecimal: true,
        });
      }
    }

    let amountDescription: string | undefined;
    if (template.amount_currency) {
      amountDescription = t.getString(
        template.amount_currency === "USD"
          ? "label-template-currency-amount-send-exact"
          : "label-template-currency-amount-send-estimate",
      );
    }

    let formattedWireReason: string | null = template.wire_reason || null;
    if (template.wire_reason && wireReasons?.length) {
      const reason = wireReasons.find(
        (r: API.InternationalWireReason) => r.code === template.wire_reason,
      );
      if (reason) {
        formattedWireReason = `${reason.code}: ${filters.humanize(
          reason.description,
        )}`;
      }
    }

    // Use server-resolved description, falling back to raw code
    const wireReasonDisplay = template.wire_reason
      ? filters.humanize(
          template.wire_reason_description ?? template.wire_reason,
        )
      : undefined;
    const templateDetailRows =
      platform === "mobile"
        ? [
            formattedAmount,
            account.getShortDescription(false),
            formattedWireReason,
            template.memo,
            template.note,
          ]
        : [
            currencyName,
            formattedAmount,
            amountDescription,
            account.getShortDescription(false),
            wireReasonDisplay,
            template.memo,
            template.note,
          ];

    const templateDetails = {
      header: t.getString("wire-templates-selector-template-details"),
      rows: templateDetailRows.filter(Boolean) as string[],
    };

    // Build address lines from swift destination address
    const addressLines = swiftDestination?.address
      ? new Address(swiftDestination.address)
          .toString()
          .split(",")
          .map((row) => row.trim())
          .filter(Boolean)
      : [];

    const recipientDetails = {
      header: t.getString("wire-templates-selector-recipient-details"),
      rows: [recipient.name, ...addressLines],
    };

    let accountIdentifierRow: string | null = null;
    if (swiftDestination?.account_identifier) {
      accountIdentifierRow =
        platform === "mobile"
          ? swiftDestination.account_identifier
          : `**${maskAccountNumber(swiftDestination.account_identifier)}`;
    }

    const bankNameRow =
      swiftDestination?.institution_name ??
      swiftDestination?.metadata?.bank_name ??
      swiftDestination?.bic ??
      null;

    // On mobile, preserve null entries so the card can render the bank icon
    // on its own line when there is no institution name/BIC.
    const bankDetailRows =
      platform === "mobile"
        ? [bankNameRow, accountIdentifierRow]
        : ([bankNameRow, accountIdentifierRow].filter(Boolean) as string[]);

    const bankDetails = {
      header: t.getString("wire-templates-selector-bank-details"),
      rows: bankDetailRows,
    };

    return {
      title: template.name,
      subheader: recipient.name,
      templateDetails,
      recipientDetails,
      bankDetails,
      bic: swiftDestination?.bic ?? null,
      institutionName:
        swiftDestination?.institution_name ?? swiftDestination?.bic ?? null,
      versionId: template.version_id,
      formattedWireReason,
    };
  }, [data, t, platform, wireReasons]);
};

export const useFilteredSwiftWireTemplateIds = (
  currency?: string,
  searchQuery?: string,
) => {
  return useSelector(
    (state) => selectFilteredSwiftWireTemplateIds(state, currency, searchQuery),
    isEqual,
  );
};

const TEMPLATE_UPDATED_FLASH_DURATION = 5 * 1000;

export const useTemplateUpdated = (
  versionId: API.SwiftWireTemplate["version_id"],
) => {
  const [showUpdated, setShowUpdated] = useState(false);

  const timeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  const clearLocalTimeout = useCallback(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
  }, []);

  useEffect(() => {
    return clearLocalTimeout;
  }, [clearLocalTimeout]);

  const flashUpdated = useCallback(() => {
    clearLocalTimeout();
    setShowUpdated(true);
    timeout.current = setTimeout(() => {
      setShowUpdated(false);
    }, TEMPLATE_UPDATED_FLASH_DURATION);
  }, [clearLocalTimeout]);

  const previousVersionId = usePrevious(versionId);
  useEffect(() => {
    if (!!previousVersionId && !!versionId && previousVersionId !== versionId) {
      flashUpdated();
    }
  }, [clearLocalTimeout, flashUpdated, previousVersionId, versionId]);

  return showUpdated;
};
